'use client';

import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { useState, useEffect } from 'react';
import Header from '@/components/Header';
import { createSupabaseClient } from '@/lib/supabase';
import { Analytics } from '@/lib/analytics';
import { ArrowRight, MessageSquareText, Users, Image as ImageIcon, Sparkles, Zap, Heart, Shield, Terminal, Code, Trophy, Target, X, Smartphone, Lock, Loader, TrendingUp, Share, Menu, CreditCard } from 'lucide-react';
import TrustBadge from '@/components/TrustBadge';
import TestimonialCarousel from '@/components/TestimonialCarousel';

// Import images to ensure proper loading
import chatImg from '../chat.jpg';
// responsesuggestions.jpg has been removed
import bioGeneratorImg from '../biogenerator.jpg';
import pictureResultsImg from '../pictureresults.jpg';
import selectPictureImg from '../selectpicture.jpg';
import newConversationImg from '../newconversation.jpg';

export default function Home() {
  const pathname = usePathname();
  const router = useRouter();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [currentAlt, setCurrentAlt] = useState<string>('');
  
  // Check if user is logged in and redirect to /conversations
  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      try {
        const supabase = createSupabaseClient();
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          router.push('/conversations');
        }
      } catch (err) {
        console.error('Error checking auth:', err);
      }
    };
    
    checkAuthAndRedirect();
  }, [router]);

  // Track analytics on page load
  useEffect(() => {
    Analytics.trackCurrentPage();
  }, []);
  
  const openLightbox = (imgSrc: string, alt: string) => {
    setCurrentImage(imgSrc);
    setCurrentAlt(alt);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent scrolling when modal is open
  };
  
  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto'; // Re-enable scrolling
  };
  
  return (
    <div className="flex flex-col min-h-screen">
      <Header currentPath={pathname} />
      <main className="flex-1">
        <section className="container grid items-center gap-8 pb-8 pt-10 md:py-16">
          {/* Hero section with a gradient backdrop */}
          <div className="relative">
            <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-primary/5 blur-3xl opacity-30 animate-float" style={{ animationDelay: '0.5s' }}></div>
            <div className="relative z-1 flex max-w-[980px] flex-col items-start gap-4">
              <div className="mb-1 flex items-center">
                <Terminal className="h-6 w-6 text-primary mr-2" />
                <p className="text-primary font-mono text-sm">ELITE DATING SYSTEM</p>
              </div>
              <h1 className="text-4xl font-extrabold leading-tight tracking-tighter md:text-5xl lg:text-6xl bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent">
                <span className="text-primary">Stop Getting Ghosted.</span> <br className="hidden sm:inline" />
                Start Getting <span className="text-primary">Dates. <Trophy className="inline h-10 w-10 ml-1 fill-primary" /></span>
              </h1>
              <p className="max-w-[700px] text-xl text-white/80">
                She matched with you for a reason. Our AI tells you exactly what to say to keep her interested and get her number. <strong>More matches. More dates. More options.</strong>
              </p>
            </div>
          </div>
          
          <div className="flex gap-4 mt-4">
            <div className="flex flex-col gap-2">
              <Link
                href="/quiz"
                className="inline-flex h-12 items-center justify-center rounded-xl bg-primary px-8 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90 animate-float"
                style={{ animationDelay: '0s' }}
              >
                Get More Dates - Start FREE <Zap className="ml-2 h-4 w-4" />
              </Link>
              <TrustBadge type="stripe" className="justify-center" />
            </div>
            <Link
              href="/login"
              className="inline-flex h-12 items-center justify-center rounded-xl glassmorphic px-8 text-sm font-medium shadow-glass transition-all hover:glassmorphic-heavy animate-float group"
              style={{ animationDelay: '0.2s' }}
            >
              Login <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Demo Video Section */}
          <div className="mt-16">
            <div className="glassmorphic rounded-2xl p-8 shadow-glass border border-primary/20">
              <h2 className="text-3xl font-bold mb-4 text-center text-white/90">
                See DateHack in <span className="text-primary">Action</span>
              </h2>
              <p className="text-center text-white/70 mb-8 max-w-2xl mx-auto">
                Watch how DateHack transforms your dating game in real-time. From conversation starters to securing dates.
              </p>
              
              {/* Video Container with Phone Frame Style */}
              <div className="flex justify-center">
                <div className="relative max-w-[400px] w-full">
                  {/* Phone frame */}
                  <div className="relative glassmorphic-light rounded-[42px] border-[8px] border-gray-800 shadow-2xl overflow-hidden">
                    {/* Top notch */}
                    <div className="absolute top-0 left-0 right-0 h-6 bg-black rounded-t-3xl z-10"></div>
                    
                    {/* YouTube Embed */}
                    <div className="relative aspect-[9/16] w-full">
                      <iframe
                        className="absolute inset-0 w-full h-full"
                        src="https://www.youtube.com/embed/fAM4i8lMzI0?autoplay=0&mute=0&controls=1&rel=0&showinfo=0&modestbranding=1&playsinline=1"
                        title="DateHack App Demo"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                    
                    {/* Bottom bar */}
                    <div className="absolute bottom-0 left-0 right-0 h-6 bg-black rounded-b-3xl z-10"></div>
                  </div>
                  
                  {/* Glow effect */}
                  <div className="absolute -inset-4 bg-primary/20 blur-3xl opacity-30 -z-10 animate-pulse"></div>
                </div>
              </div>
              
              {/* Call to action below video */}
              <div className="text-center mt-8">
                <p className="text-white/60 text-sm mb-4">Tired of getting left on read?</p>
                <div className="flex flex-col items-center gap-2">
                  <Link
                    href="/quiz"
                    className="inline-flex h-11 items-center justify-center rounded-xl bg-primary/90 px-6 text-sm font-medium text-primary-foreground shadow transition-all hover:bg-primary hover:scale-105"
                  >
                    Find Out Why She's Not Responding <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                  <TrustBadge type="stripe" />
                </div>
              </div>
            </div>
          </div>

          {/* Dating Apps Compatibility Section */}
          <div className="mt-16">
            <div className="glassmorphic rounded-2xl p-8 shadow-glass border border-primary/20 text-center">
              <h2 className="text-2xl font-bold mb-8 text-white/90">
                Works on <span className="text-primary">All Major Dating Apps</span>
              </h2>

              {/* Logo Grid */}
              <div className="flex flex-wrap justify-center items-center gap-10 md:gap-16">
                <div className="relative w-36 h-20 md:w-52 md:h-28 transition-transform hover:scale-110">
                  <Image
                    src="/images/dating-apps/tinder.png"
                    alt="Tinder"
                    fill
                    style={{ objectFit: 'contain' }}
                  />
                </div>
                <div className="relative w-36 h-20 md:w-52 md:h-28 transition-transform hover:scale-110">
                  <Image
                    src="/images/dating-apps/bumble.png"
                    alt="Bumble"
                    fill
                    style={{ objectFit: 'contain' }}
                  />
                </div>
                <div className="relative w-36 h-20 md:w-52 md:h-28 transition-transform hover:scale-110 bg-white/90 rounded-lg p-2">
                  <Image
                    src="/images/dating-apps/hinge.png"
                    alt="Hinge"
                    fill
                    style={{ objectFit: 'contain' }}
                  />
                </div>
                <div className="relative w-36 h-20 md:w-52 md:h-28 transition-transform hover:scale-110">
                  <Image
                    src="/images/dating-apps/okcupid.png"
                    alt="OkCupid"
                    fill
                    style={{ objectFit: 'contain' }}
                  />
                </div>
                <div className="relative w-36 h-20 md:w-52 md:h-28 transition-transform hover:scale-110 bg-white/90 rounded-lg p-2">
                  <Image
                    src="/images/dating-apps/badoo.png"
                    alt="Badoo"
                    fill
                    style={{ objectFit: 'contain' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Main features showcase - All Mobile Tools Together */}
          <div className="mt-20">
            <div className="glassmorphic rounded-2xl p-8 shadow-glass border border-primary/20">
              <h2 className="text-3xl font-bold mb-2 text-center text-white/90">The Elite Dating Arsenal</h2>
              <p className="text-center text-white/70 mb-12 max-w-2xl mx-auto">
                Access the same tools and techniques used by the top 1% of men who dominate the online dating scene
              </p>
              
              <div className="flex flex-wrap justify-center gap-12">
                {/* Phone mockups in a row */}
                <div className="flex flex-col items-center">
                  <div className="relative glassmorphic-light rounded-[32px] border-[6px] border-gray-800 w-[200px] h-[380px] overflow-hidden mb-4 shadow-lg">
                    <div className="absolute top-0 left-0 right-0 h-4 bg-black rounded-t-2xl z-10"></div>
                    <div className="cursor-pointer h-full" onClick={() => openLightbox(chatImg.src, "DateHack AI-powered conversation interface")}>
                      <Image 
                        src={chatImg} 
                        alt="DateHack AI-powered conversation interface" 
                        fill 
                        style={{ objectFit: 'cover' }}
                        className="hover:scale-[1.05] transition-transform duration-500"
                      />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                      <div className="flex items-center gap-2">
                        <MessageSquareText className="h-3 w-3 text-primary" />
                        <p className="text-xs text-white/90 font-mono">// message hacker</p>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Conversation Hacker</h3>
                  <p className="text-white/70 text-center max-w-xs">
                    Instantly know the perfect thing to say. Our AI crafts replies based on proven patterns that escalate attraction and <strong>secure dates</strong>, giving you the conversational upper hand.
                  </p>
                  <p className="text-xs text-white/60 mt-2 max-w-xs flex items-center justify-center">
                    <Lock className="h-3 w-3 mr-1.5 text-primary flex-shrink-0" /> Your chat data is processed securely.
                  </p>
                  {/* New Interest Indicator Highlight */}
                  <div className="flex items-start gap-2 mt-3 max-w-xs px-4 w-full"> {/* w-full with max-w-xs and px-4 centers content with items-start */}
                    <TrendingUp className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
                    <p className="text-xs text-white/70 text-left">
                      <strong>Bonus:</strong> Our live Interest Indicator gauges her engagement, so you always know where you stand.
                    </p>
                  </div>
                  <Link href="/quiz" className="text-primary flex items-center text-sm font-medium hover:underline mt-4">
                    Stop Getting Ghosted <ArrowRight className="ml-1 h-3 w-3" />
                  </Link>
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="relative glassmorphic-light rounded-[32px] border-[6px] border-gray-800 w-[200px] h-[380px] overflow-hidden mb-4 shadow-lg">
                    <div className="absolute top-0 left-0 right-0 h-4 bg-black rounded-t-2xl z-10"></div>
                    <div className="cursor-pointer h-full" onClick={() => openLightbox(bioGeneratorImg.src, "AI bio generator")}>
                      <Image 
                        src={bioGeneratorImg} 
                        alt="AI bio generator" 
                        fill 
                        style={{ objectFit: 'cover' }}
                        className="hover:scale-[1.05] transition-transform duration-500"
                      />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                      <div className="flex items-center gap-2">
                        <Trophy className="h-3 w-3 text-primary" />
                        <p className="text-xs text-white/90 font-mono">// bio engineer</p>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Elite Bio Generator</h3>
                  <p className="text-white/70 text-center max-w-xs">
                    Command attention from the start. Engineer a bio with precise psychological triggers that high-quality matches find irresistible. Make your profile <strong>their first stop</strong>.
                  </p>
                  <Link href="/quiz" className="text-primary flex items-center text-sm font-medium hover:underline mt-4">
                    Stop Getting Ghosted <ArrowRight className="ml-1 h-3 w-3" />
                  </Link>
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="relative glassmorphic-light rounded-[32px] border-[6px] border-gray-800 w-[200px] h-[380px] overflow-hidden mb-4 shadow-lg">
                    <div className="absolute top-0 left-0 right-0 h-4 bg-black rounded-t-2xl z-10"></div>
                    <div className="cursor-pointer h-full" onClick={() => openLightbox(newConversationImg.src, "New conversation interface")}>
                      <Image 
                        src={newConversationImg} 
                        alt="New conversation interface" 
                        fill 
                        style={{ objectFit: 'cover' }}
                        className="hover:scale-[1.05] transition-transform duration-500"
                      />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                      <div className="flex items-center gap-2">
                        <Loader className="h-3 w-3 text-primary" />
                        <p className="text-xs text-white/90 font-mono">// opener generator</p>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Opener Generator</h3>
                  <p className="text-white/70 text-center max-w-xs">
                    Never get ignored again. Deploy openers that cut through the noise, spark intrigue, and <strong>demand a reply</strong>. Your first impression, mastered.
                  </p>
                  <Link href="/quiz" className="text-primary flex items-center text-sm font-medium hover:underline mt-4">
                    Stop Getting Ghosted <ArrowRight className="ml-1 h-3 w-3" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
          
          {/* Advanced Desktop Analysis - Combined Picture Analysis Feature */}
          <div className="mt-16">
            <div className="glassmorphic rounded-2xl p-8 shadow-glass border border-primary/20">
              <h2 className="text-3xl font-bold mb-4 text-center text-white/90">Advanced <span className="text-primary">Photo</span> Analysis</h2>
              <p className="text-center text-white/70 mb-10 max-w-2xl mx-auto">
                Stop guessing if your photos are working against you. Our proprietary algorithms decode the subtle signals—expressions, body language, even background details—that determine success or failure in the dating marketplace, turning uncertainty into actionable insights.
              </p>
              
              <div className="flex flex-col gap-10">
                {/* Desktop tools with description */}
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <div className="md:w-1/3">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-primary/20 text-primary-foreground rounded-full p-2">
                        <Target className="h-5 w-5" />
                      </div>
                      <h3 className="text-xl font-semibold">Picture Optimization System</h3>
                    </div>
                    <p className="text-white/80 mb-6">
                      No more wondering if your pictures are hurting your chances. See your photos through *her* eyes. Our advanced AI decodes what makes a picture irresistible—from facial expressions to body language—giving you the power to optimize your visual appeal and <strong>make every swipe a potential match</strong>.
                    </p>
                    <p className="text-xs text-white/60 mb-4 flex items-start">
                      <Lock className="h-3 w-3 mr-1.5 text-primary flex-shrink-0 mt-1" /> Your photos are analyzed securely and privately.
                    </p>
                    <div className="flex flex-col gap-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-primary/20 text-primary-foreground rounded-full p-2 mt-0.5">
                          <Target className="h-4 w-4" />
                        </div>
                        <div>
                          <h4 className="text-white font-medium text-base">Facial Analysis Engine</h4>
                          <p className="text-white/70 text-sm">
                            Detects optimal facial expressions that maximize attraction response
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-primary/20 text-primary-foreground rounded-full p-2 mt-0.5">
                          <Lock className="h-4 w-4" />
                        </div>
                        <div>
                          <h4 className="text-white font-medium text-base">Body language analysis</h4>
                          <p className="text-white/70 text-sm">
                            Analyzes your body language to determine if you're sending the right signals
                          </p>
                        </div>
                      </div>
                      <Link href="/quiz" className="text-primary flex items-center text-sm font-medium hover:underline mt-2">
                        Stop Getting Ghosted <ArrowRight className="ml-1 h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                  
                  {/* Two analysis screens side by side */}
                  <div className="md:w-2/3 flex flex-col md:flex-row gap-4">
                    <div className="md:w-1/2">
                      <div className="relative glassmorphic-light rounded-xl overflow-hidden border border-primary/10 shadow-lg">
                        <div className="absolute top-0 left-0 right-0 h-8 bg-gray-800/60 z-10 flex items-center px-4">
                          <div className="flex gap-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          </div>
                          <div className="mx-auto">
                            <p className="text-xs text-white/80 font-mono">select_image.exe</p>
                          </div>
                        </div>
                        <div className="cursor-pointer" 
                            onClick={() => openLightbox(selectPictureImg.src, "Picture selection interface")}>
                          <Image 
                            src={selectPictureImg} 
                            alt="Picture selection interface" 
                            width={400}
                            height={300}
                            className="hover:scale-[1.03] transition-transform duration-500"
                          />
                        </div>
                      </div>
                      <p className="text-white/60 text-xs text-center mt-2">Step 1: Intelligent Image Selection</p>
                    </div>
                    
                    <div className="md:w-1/2">
                      <div className="relative glassmorphic-light rounded-xl overflow-hidden border border-primary/10 shadow-lg">
                        <div className="absolute top-0 left-0 right-0 h-8 bg-gray-800/60 z-10 flex items-center px-4">
                          <div className="flex gap-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          </div>
                          <div className="mx-auto">
                            <p className="text-xs text-white/80 font-mono">analysis_result.exe</p>
                          </div>
                        </div>
                        <div className="cursor-pointer" 
                            onClick={() => openLightbox(pictureResultsImg.src, "Picture analysis results")}>
                          <Image 
                            src={pictureResultsImg} 
                            alt="Picture analysis results"
                            width={400}
                            height={300}
                            className="hover:scale-[1.03] transition-transform duration-500"
                          />
                        </div>
                      </div>
                      <p className="text-white/60 text-xs text-center mt-2">Step 2: Advanced Attraction Analysis</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* How It Works Section */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold mb-10 text-center text-white/90">The DateHack App System</h2>
            <div className="grid gap-8 md:grid-cols-3">
              <div className="flex flex-col items-center text-center glassmorphic-light rounded-xl p-6 animate-float border border-primary/10" style={{ animationDelay: '0.3s' }}>
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/20 text-primary-foreground mb-4">
                  <ImageIcon className="h-8 w-8 text-primary" />
                </div>
                <h4 className="text-lg font-medium mb-2">Step 1: Craft Your Irresistible Profile</h4>
                <p className="text-white/70">
                  Stop blending in. We help you forge an AI-engineered bio and pick pics that grab attention & make them *want* to swipe right. First impressions? Nailed.
                </p>
              </div>
              <div className="flex flex-col items-center text-center glassmorphic-light rounded-xl p-6 animate-float border border-primary/10" style={{ animationDelay: '0.5s' }}>
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/20 text-primary-foreground mb-4">
                  <MessageSquareText className="h-8 w-8 text-primary" />
                </div>
                <h4 className="text-lg font-medium mb-2">Step 2: Become a Conversation Decoder</h4>
                <p className="text-white/70">
                  Feed the AI your chats (easy peasy!). It learns what makes your matches tick, then hands you their psychological playbook. It's like having a dating sensei in your pocket, 24/7.
                </p>
              </div>
              <div className="flex flex-col items-center text-center glassmorphic-light rounded-xl p-6 animate-float border border-primary/10" style={{ animationDelay: '0.7s' }}>
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/20 text-primary-foreground mb-4">
                  <Heart className="h-8 w-8 text-primary" />
                </div>
                <h4 className="text-lg font-medium mb-2">Step 3: Turn Matches into Actual Dates</h4>
                <p className="text-white/70">
                  No more chats fizzling out. Deploy our battle-tested message sequences that spark excitement, build real connection, and get them eager to meet up. Get ready to clear your schedule!
                </p>
              </div>
            </div>
          </div>
          
          {/* Benefits Section */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold mb-10 text-center text-white/90">The DateHack App Advantage</h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <div className="glassmorphic-light rounded-xl p-5 flex animate-float border border-primary/10" style={{ animationDelay: '0.2s' }}>
                <div className="bg-primary/20 text-primary-foreground rounded-full p-2 h-fit mr-4">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="text-lg font-medium mb-1">Magnetic Messages: Get Her Hooked</h4>
                  <p className="text-sm text-white/70">Forget boring small talk. Our AI crafts messages that hit different – making her smile, think, and *actually want* to reply. Watch your response rate <strong>explode</strong>.</p>
                </div>
              </div>
              
              <div className="glassmorphic-light rounded-xl p-5 flex animate-float border border-primary/10" style={{ animationDelay: '0.4s' }}>
                <div className="bg-primary/20 text-primary-foreground rounded-full p-2 h-fit mr-4">
                  <Zap className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="text-lg font-medium mb-1">The Cheat Code for Dating Success</h4>
                  <p className="text-sm text-white/70">Stop shooting in the dark. We've analyzed what *works* (thousands of times over). Get the proven playbook to navigate chats and <strong>get way more 'Yes!' to dates</strong>.</p>
                </div>
              </div>
              
              <div className="glassmorphic-light rounded-xl p-5 flex animate-float border border-primary/10" style={{ animationDelay: '0.6s' }}>
                <div className="bg-primary/20 text-primary-foreground rounded-full p-2 h-fit mr-4">
                  <Heart className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="text-lg font-medium mb-1">Finally Meet *Her* (Not Just Anyone)</h4>
                  <p className="text-sm text-white/70">No more swiping just to swipe. Attract and connect with women who genuinely excite you. Imagine dates you <strong>actually look forward to</strong>. Yeah, that.</p>
                </div>
              </div>
              
              <div className="glassmorphic-light rounded-xl p-5 flex animate-float border border-primary/10" style={{ animationDelay: '0.8s' }}>
                <div className="bg-primary/20 text-primary-foreground rounded-full p-2 h-fit mr-4">
                  <Shield className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="text-lg font-medium mb-1">Your Personality, Supercharged</h4>
                  <p className="text-sm text-white/70">This isn't about being fake. It's about being your *most charming, witty self*, effortlessly. Our AI gives you the killer lines, you bring the authenticity.</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Install on Mobile Section */}
          <div className="mt-16">
            <div className="glassmorphic rounded-2xl p-8 shadow-glass border border-primary/20 text-center animate-float" style={{ animationDelay: '0.2s' }}>
              <div className="flex justify-center mb-6">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/20 text-primary-foreground">
                  <Smartphone className="h-8 w-8 text-primary" />
                </div>
              </div>
              <h2 className="text-3xl font-bold mb-4 text-white/90">Get DateHack on Your Phone</h2>
              <p className="text-white/70 mb-5 max-w-xl mx-auto">
                Want quick access? Add DateHack to your phone's home screen. It's like having the app icon right there for easy advice anytime.
              </p>
              <p className="text-white/70 text-sm max-w-xl mx-auto">
                <strong>How to add:</strong> Open DateHack in your mobile browser (e.g., Safari, Chrome). Tap your browser's 'Share' icon (often an arrow pointing upwards from a box) or 'Menu' button (usually three dots or lines), then select an option like "Add to Home Screen" or "Install app".
              </p>
            </div>
          </div>
          
          {/* Social Proof / Testimonials Section */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold mb-10 text-center text-white/90">
              Don't Just Take Our Word For It...
            </h2>
            
            <TestimonialCarousel />
            
            {/* Call to action after testimonials */}
            <p className="text-center text-primary mt-10 font-semibold text-lg animate-float" style={{ animationDelay: '0.7s' }}>
              The Dating Game Just Got Easier. <strong className="text-white font-bold">Claim Your Unfair Advantage.</strong>
            </p> 
          </div>
          
          {/* CTA Section */}
          <div className="mt-16">
            <div className="glassmorphic rounded-2xl p-8 text-center border border-primary/20">
              <h2 className="text-3xl font-bold mb-4 text-white/90">Other Guys Are Still Sending "Hey"</h2>
              <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
                While they're getting ignored, you'll have AI-powered responses that actually get her number. Stop wasting matches. Start getting dates.
              </p>
              <Link
                href="/quiz"
                className="inline-flex h-12 items-center justify-center rounded-xl bg-primary px-8 text-base font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90 animate-float"
              >
                Get Your Unfair Advantage - FREE for 7 Days
              </Link>
              <div className="flex flex-col items-center gap-2 mt-3">
                <p className="text-sm text-white/70">✓ Payment method required • ✓ Free for 7 days • ✓ Cancel anytime</p>
                <TrustBadge type="stripe" />
              </div>
            </div>
          </div>
        </section>
      </main>
      <footer className="border-t border-border/20 py-6 glassmorphic-light">
        <div className="container flex flex-col items-center justify-between gap-4 md:h-16 md:flex-row">
          <div className="flex flex-col items-center md:items-start gap-2">
            <p className="text-center text-sm text-white/60">
              <Lock className="inline h-3.5 w-3.5 mr-1 text-primary relative -top-0.5" />
              &copy; {new Date().getFullYear()} DateHack App. All rights reserved. <br className="sm:hidden"/> Your data is 100% private and secure.
            </p>
            <div className="flex gap-4">
              <TrustBadge type="ssl" />
              <TrustBadge type="stripe" />
              <TrustBadge type="secure" text="256-bit Encryption" />
            </div>
          </div>
          <div className="flex items-center space-x-6">
            <a 
              href="mailto:datehackapp@gmail.com" 
              className="text-sm text-white/60 hover:text-white transition-colors"
            >
              Contact
            </a>
            <a 
              href="/privacy" 
              className="text-sm text-white/60 hover:text-white transition-colors"
            >
              Privacy
            </a>
          </div>
        </div>
      </footer>
      
      {/* Image Lightbox/Modal */}
      {lightboxOpen && currentImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Glassmorphic backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" 
            onClick={closeLightbox}
          />
          
          {/* Modal content */}
          <div className="relative max-w-[90vw] max-h-[90vh] animate-in fade-in zoom-in duration-200">
            <button 
              className="absolute -top-12 right-0 text-white/80 hover:text-white p-3 glassmorphic-light rounded-lg transition-all duration-200 hover:glassmorphic"
              onClick={(e) => {
                e.stopPropagation();
                closeLightbox();
              }}
            >
              <X className="h-6 w-6" />
              <span className="sr-only">Close</span>
            </button>
            <div className="glassmorphic-heavy p-3 rounded-xl shadow-glass-xl overflow-hidden">
              <img 
                src={currentImage} 
                alt={currentAlt}
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 